import { createClient as createSupabaseClient, type SupabaseClient } from '@supabase/supabase-js'
import { Database } from './types'
import { createTimeoutFetch } from './timeoutFetch'
import { createAuthRetryFetch } from './authRetryFetch'
import { getAccessToken, onTokenChange } from '@/lib/auth/accessToken'

// The browser never runs Supabase auth. The session is an HttpOnly cookie that
// only the server refreshes (middleware + /api/auth/*); this client borrows the
// current access token from src/lib/auth/accessToken.ts. With `accessToken`
// set, supabase-js disables `supabase.auth` entirely – sign-in, sign-out and
// session recovery go through /api/auth/* routes. That removes the auth lock,
// cross-tab refresh races and device-clock expiry decisions behind the
// 2026-06-11, 2026-08-28, 2026-09-17/24 and 2026-10-11 incidents.
// See docs/superpowers/plans/2026-10-11-server-held-session.md.
//
// `global.fetch`: requests are capped by timeoutFetch (a hung request must not
// stall a page), and a 401 on a user-token request gets one token refresh and
// one resend (authRetryFetch).
const anonKey = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY!

let instance: SupabaseClient<Database> | null = null

function build(): SupabaseClient<Database> {
  const client = createSupabaseClient<Database>(process.env.NEXT_PUBLIC_SUPABASE_URL!, anonKey, {
    accessToken: () => getAccessToken(),
    global: {
      fetch: createAuthRetryFetch({ fetchImpl: createTimeoutFetch(), getToken: getAccessToken, anonKey }),
    },
  })
  // supabase-js seeds realtime once with setAuth(token), which realtime-js
  // treats as a manually set token and never refreshes – live chat would die
  // when that token expires. setAuth() with no argument goes back to the
  // accessToken callback and pushes the fresh token to open channels.
  onTokenChange(() => {
    void client.realtime.setAuth()
  })
  return client
}

export const createClient = (): SupabaseClient<Database> => (instance ??= build())

export const supabase = createClient()
